// Selectors that look at an attribute the rewriter changed see the proxied value: a page asking
// for script[src="https://cdn.example/x.js"] found nothing, although it had that script. React 19
// (Next.js) looks up its async scripts and stylesheets that way before inserting them, so it
// inserted every one a second time and each script ran twice (Primark's OneTrust then threw and
// its footer did not render). The rewriter keeps the original value in scramjet-attr-<name>, so
// such a selector is asked of that instead, and of the attribute itself where nothing was
// rewritten (no scramjet-attr-<name>, e.g. a data: address or a tag the rules leave alone).

import { ScramjetClient } from "@client/index";

// The attributes the rules rewrite (shared/htmlRules.ts) whose setters element.ts also hooks, so
// their scramjet-attr- copy stays current. style and target are rewritten too, but pages change
// them through the CSSOM or plain properties, which would leave the copy behind.
const REWRITTEN = new Set([
	"src",
	"href",
	"data",
	"action",
	"formaction",
	"poster",
	"srcset",
	"imagesrcset",
	"ping",
	"srcdoc",
	"integrity",
	"nonce",
]);

// [name], [name=value], [name^="value" i] ...: the name, the operator and value with its flag.
const ATTRIBUTE =
	/\[\s*([A-Za-z][\w-]*)\s*((?:[~|^$*]?=)\s*(?:"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|[^\s\]"']+)(?:\s+[iIsS])?)?\s*\]/g;

const translated = new Map<string, string>();

export function translateSelector(selector: string): string {
	if (!selector.includes("[")) return selector;
	const known = translated.get(selector);
	if (known !== undefined) return known;

	const result = selector.replace(
		ATTRIBUTE,
		(whole, name: string, test: string | undefined) => {
			const lower = name.toLowerCase();
			if (!REWRITTEN.has(lower)) return whole;
			const rest = test ? test.trim() : "";

			return `:is([scramjet-attr-${lower}${rest}],[${lower}${rest}]:not([scramjet-attr-${lower}]))`;
		}
	);
	if (translated.size > 1000) translated.clear();
	translated.set(selector, result);

	return result;
}

export default function (client: ScramjetClient, self: any) {
	const handler = {
		apply(ctx: any) {
			if (typeof ctx.args[0] === "string")
				ctx.args[0] = translateSelector(ctx.args[0]);
		},
	};
	const targets = [
		"Document.prototype.querySelector",
		"Document.prototype.querySelectorAll",
		"DocumentFragment.prototype.querySelector",
		"DocumentFragment.prototype.querySelectorAll",
		"Element.prototype.querySelector",
		"Element.prototype.querySelectorAll",
		"Element.prototype.matches",
		"Element.prototype.closest",
		"Element.prototype.webkitMatchesSelector",
	].filter((path) => {
		const [owner, , name] = path.split(".");

		return self[owner] && name in self[owner].prototype;
	});
	client.Proxy(targets, handler);
}
